/**
 * DemoRoleSwitcher.tsx — Demo Mode Role Selector
 *
 * A small fixed control (top-right) that lets the presenter switch the demo
 * user's role between the three application roles. The selection is persisted
 * to localStorage under the key `demo_role`; changing it reloads the page so
 * DemoAuthProvider re-mints the fake Firebase user with the new `appRole` and
 * the whole app re-renders (route guards, dashboards) as that role.
 *
 * Rendered by DemoAuthProvider so it appears on every page without any change
 * to App.tsx.
 */
import { useState } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
  DEMO_GUEST,
  DEMO_ROLE_STORAGE_KEY,
  isDemoGuest,
  readDemoRole,
  type DemoRoleSelection,
} from '@/demo/demo-role';

// Ordered for display; labels kept short for the compact control.
const ROLE_OPTIONS: ReadonlyArray<{ value: DemoRoleSelection; label: string }> = [
  { value: DEMO_GUEST, label: 'Visitor' },
  { value: 'donor', label: 'Donor' },
  { value: 'org_admin', label: 'Org Admin' },
  { value: 'platform_admin', label: 'Platform Admin' },
];

/**
 * Fixed top-right role selector. Writing a new role persists it and reloads so
 * the auth store is re-seeded from scratch (the store has no partial update for
 * a single field, so a reload is the simplest correct re-mint path).
 */
/** localStorage key for the switcher's dragged position. */
const DEMO_SWITCHER_POS_KEY = 'demo_switcher_pos';

function readSavedPos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(DEMO_SWITCHER_POS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as { x?: number; y?: number };
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
      // Clamp on-screen so a saved position from a larger window stays reachable.
      return {
        x: Math.min(Math.max(parsed.x, 0), window.innerWidth - 80),
        y: Math.min(Math.max(parsed.y, 0), window.innerHeight - 60),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function DemoRoleSwitcher(): JSX.Element {
  const [role, setRole] = useState<DemoRoleSelection>(() =>
    isDemoGuest() ? DEMO_GUEST : readDemoRole(),
  );
  // Draggable position — null means "use the default bottom-left docking".
  const [pos, setPos] = useState<{ x: number; y: number } | null>(() => readSavedPos());

  /** Drag via the DEMO ROLE label (pointer events; persists to localStorage). */
  const handleDragStart = (e: React.PointerEvent<HTMLElement>): void => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const el = (e.currentTarget.closest('[data-demo-switcher]') as HTMLElement | null);
    if (el === null) return;
    const rect = el.getBoundingClientRect();
    const origin = { x: rect.left, y: rect.top };

    function onMove(ev: PointerEvent): void {
      const next = {
        x: Math.min(Math.max(origin.x + (ev.clientX - startX), 0), window.innerWidth - rect.width),
        y: Math.min(Math.max(origin.y + (ev.clientY - startY), 0), window.innerHeight - rect.height),
      };
      setPos(next);
    }
    function onUp(ev: PointerEvent): void {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const finalPos = {
        x: Math.min(Math.max(origin.x + (ev.clientX - startX), 0), window.innerWidth - rect.width),
        y: Math.min(Math.max(origin.y + (ev.clientY - startY), 0), window.innerHeight - rect.height),
      };
      localStorage.setItem(DEMO_SWITCHER_POS_KEY, JSON.stringify(finalPos));
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: DemoRoleSelection | null,
  ): void => {
    // ToggleButtonGroup passes null when the active button is clicked again;
    // ignore that so a role is always selected.
    if (next === null || next === role) {
      return;
    }
    localStorage.setItem(DEMO_ROLE_STORAGE_KEY, next);
    setRole(next);
    // Navigate to the selected role's home so the URL and the visible surface
    // change together (donor → /dashboard, org_admin → /organization,
    // platform_admin → /admin). A full navigation (not SPA route change) so
    // DemoAuthProvider re-mints the fake user with the new appRole.
    const ROLE_HOME: Record<DemoRoleSelection, string> = {
      [DEMO_GUEST]: '/',
      donor: '/donor',
      org_admin: '/organization',
      platform_admin: '/admin',
    };
    window.location.assign(ROLE_HOME[next]);
  };

  return (
    <Paper
      elevation={4}
      role="region"
      aria-label="Demo role selector"
      data-demo-switcher
      sx={{
        position: 'fixed',
        // Default: bottom-left docking; once dragged, the saved position wins.
        ...(pos === null ? { bottom: 48, left: 8 } : { top: pos.y, left: pos.x }),
        // Below MUI's modal layer (1300): dialogs, menus, and drawers always
        // stack ABOVE the switcher, so it can never block an open dialog.
        zIndex: 1250,
        px: 1.5,
        py: 1,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          variant="caption"
          onPointerDown={handleDragStart}
          sx={{
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: 'text.secondary',
            cursor: 'grab',
            userSelect: 'none',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          ⠿ DEMO ROLE — drag me
        </Typography>
        <ToggleButtonGroup
          value={role}
          exclusive
          size="small"
          onChange={handleChange}
          aria-label="Select demo role"
        >
          {ROLE_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none' }}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Paper>
  );
}
